import { EnvironmentProviders, inject, Injectable, provideEnvironmentInitializer } from '@angular/core';
import { stripControlCharacters } from 'app/modules/shared/utils/third-party-data';

/** Longest value a user can type into a single-line / multi-line field that sets no limit of its own. */
const DEFAULT_MAX_LENGTH_SINGLE_LINE = 255;
const DEFAULT_MAX_LENGTH_MULTI_LINE = 20000;

/** Single-line text fields. Passwords, numbers, dates, files, ... are left alone. */
const TEXT_INPUTS = 'input:not([type]), input[type=text], input[type=search], input[type=tel], input[type=url], input[type=email]';
const LIMITED_FIELDS = `${TEXT_INPUTS}, textarea`;

/**
 * Basic input checks for every text field of the app (BSI TR-03161-2 O.Source_1), as a second line of
 * defence next to the validators of the forms and the checks of the backend:
 *
 *  - A field that sets no `maxlength` of its own gets a generous one (255 characters for single-line
 *    fields, 20,000 for text areas), so unbounded input cannot be typed or pasted. Values that already
 *    exist (loaded from the backend) are not touched.
 *  - Control characters (NUL, escape sequences, ...) that are typed or pasted into a text field are
 *    removed before the form sees the value; line breaks and tabs stay in text areas.
 *
 * A field that sets its own `maxlength` keeps it. Password fields are never changed.
 */
@Injectable({ providedIn: 'root' })
export class InputSafetyService {
    private _observer: MutationObserver | null = null;
    private readonly _onInput = (event: Event): void => this._clean(event);

    start(): void {
        if (this._observer || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
            return;
        }

        this._limit(document.body);

        this._observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        this._limit(node);
                    }
                });
            }
        });
        this._observer.observe(document.body, { childList: true, subtree: true });

        // Capture phase: runs before the handlers of Angular's form directives, which then read the cleaned value
        document.addEventListener('input', this._onInput, true);
    }

    stop(): void {
        this._observer?.disconnect();
        this._observer = null;
        document.removeEventListener('input', this._onInput, true);
    }

    private _limit(root: HTMLElement): void {
        if (root.matches(LIMITED_FIELDS)) {
            this._setMaxLength(root);
        }
        if (root.childElementCount) {
            root.querySelectorAll(LIMITED_FIELDS).forEach((element) => this._setMaxLength(element));
        }
    }

    private _setMaxLength(element: Element): void {
        if (!element.hasAttribute('maxlength')) {
            const multiLine = element.tagName === 'TEXTAREA';
            element.setAttribute('maxlength', String(multiLine ? DEFAULT_MAX_LENGTH_MULTI_LINE : DEFAULT_MAX_LENGTH_SINGLE_LINE));
        }
    }

    private _clean(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) || (event as InputEvent).isComposing) {
            return;
        }
        if (target instanceof HTMLInputElement && !target.matches(TEXT_INPUTS)) {
            return;
        }
        const cleaned = stripControlCharacters(target.value, target instanceof HTMLTextAreaElement);
        if (cleaned !== target.value) {
            target.value = cleaned;
        }
    }
}

/**
 * Starts the {@link InputSafetyService} when the app starts.
 */
export function provideInputSafety(): EnvironmentProviders {
    return provideEnvironmentInitializer(() => inject(InputSafetyService).start());
}
